import React, { useState, useEffect, useRef } from "react";

const FAQModal = ({ isOpen, onClose, mobile }) => {
  const [showNavButton, setShowNavButton] = useState(false);
  const scrollContainerRef = useRef(null);
  const navSectionRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // If nav section is NOT visible, show the button
        setShowNavButton(!entry.isIntersecting);
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.1
      }
    );

    if (navSectionRef.current) {
      observer.observe(navSectionRef.current);
    }

    return () => observer.disconnect();
  }, [isOpen]);

  if (!isOpen) return null;

  const sections = [
    {
      title: "📸 The Golden Rule: Input Quality",
      items: [
        "Selfies are for social media, not professional AI generation. Avoid them for best results.",
        "High-resolution photos taken with a rear camera (not front-facing) provide the best biometric data.",
        "Ensure even lighting on the face. Harsh shadows or 'half-lit' faces will confuse the AI.",
        "Avoid filters. AI needs to see your real skin texture to recreate it accurately.",
        "Look directly at the camera. If your eyes are looking away, the AI might struggle with eye contact.",
        "Neutral backgrounds in source photos help the AI isolate your features more cleanly.",
        "Distance matters: A mid-shot (chest up) is better than a tight close-up for source data.",
        "Wear minimal makeup in source photos if you want the AI to handle the 'glam' via Style Builder.",
        "Glasses: If you want to keep your glasses, ensure there's no glare on the lenses in the source photo.",
        "Hair: Ensure your hair isn't covering significant parts of your face in the source image.",
        "Avoid wearing hats or large headbands in your source photo unless you want them in the output.",
        "Ensure your face is clean and dry; excessive sweat or oil can create unwanted highlights.",
        "Use a photo where you have a neutral or slight smile to give the AI a good baseline.",
        "Avoid photos where you are making 'duck faces' or extreme expressions.",
        "Ensure the photo is sharp; motion blur in the source will lead to a 'painterly' look in the AI output."
      ]
    },
    {
      title: "👔 Mastering the Style Builder",
      items: [
        "Style Builder is your virtual wardrobe. Use it to define the 'Vibe' of your shoot.",
        "Group Reference Image: Upload a composition reference to guide the AI on how subjects should be arranged in a group portrait.",
        "Figure Mapping: Map 'Figure X' from your reference to a specific 'Identifier' (e.g., 'sub1') to ensure the right person is in the right spot.",
        "Removing Figures: If your reference image has more people than your group, simply leave them unmapped or use the 'Remove' option in the detailed mapping modal.",
        "One-to-One Rule: Each figure can only be mapped to one identifier, and vice-versa, to maintain logical consistency and prevent identity merging.",
        "Global Directives: Use the 'Global Style Directive' box to add overarching instructions like 'Everyone is wearing sunglasses' or 'Add a dog to the bottom right'.",
        "To get a specific suit color, select 'Custom' in the color picker and provide the hex code.",
        "Want a specific tie pattern? Use the 'Tie Style' dropdown to choose between plain, striped, or checked.",
        "For female professional attire, the 'Blouse Style' allows for buttoned-down, v-neck, or rounded collars.",
        "Style Builder can handle 'Exotic Jewelry'. Select the jewelry options to add necklaces or earrings.",
        "Material Fidelity: The AI captures fabric textures. A 'Silk' blouse will look different from a 'Cotton' one.",
        // "Layering: You can specify a blazer over a shirt or just a standalone professional top.",
        // "Cultural Attire: Use the specialized sections for sarees, lehengas, or other traditional professional wear.",
        "Indian Decency Standards: For cultural attire like sarees, the AI is optimized to ensure the navel and midriff are modestly covered for a professional look.",
        "Uniforms: The 'Uniform' mode is perfect for medical, military, or corporate standardized looks.",
        "Graduation: Use the 'Graduation' mode to generate high-quality commencement portraits with custom gown colors.",
        "Sweater Styles: Choose between crew-neck, v-neck, or turtle-neck for a more approachable corporate look.",
        "Shirt Patterns: Solid colors are safest, but subtle stripes or checks add a touch of personality."
        // "Blouse Colors: Use soft pastels for a friendly look, or bold jewel tones for a high-authority presence.",
        // "Tassel Side: For graduation shots, ensure you specify the correct side for the tassel based on your degree status.",
        // "Gown Closure: Choose between open or closed gowns for your graduation portrait.",
      ]
    },
    {
      title: "🚲 Creative Scenarios: Bikes, Horses & Fashion",
      items: [
        "Sitting on a Bike: Use the 'Posture' settings or provide a reference image of someone on a bike.",
        "Riding a Horse: This requires a full-body aspect ratio (9:16) and a clear reference image for the pose.",
        "Exotic Jewelry: In the Style Builder, ensure 'Jewelry' is enabled. The AI will replicate the style from your reference.",
        "Fashion Attire: Use the 'Casual/Fashion' mode in Style Builder for non-corporate, high-fashion looks.",
        "Dynamic Poses: If you want to be 'walking' or 'leaning', use the 'Posture' reference upload.",
        "Background Integration: For a bike shot, use a 'City Street' or 'Coastal Road' background.",
        "Lighting Match: The AI will automatically adjust your face's lighting to match the 'Golden Hour' or 'Studio' background.",
        "Prop Interaction: When using props like a bike, the AI calculates realistic contact shadows.",
        "Action Shots: While possible, static 'posed' action shots (like sitting on a moving bike) work best.",
        "Full Body: Always use 9:16 aspect ratio for scenarios involving horses, bikes, or full fashion ensembles.",
        "Motorcycle Gear: If you want to wear a helmet, the AI can render it, but it may obscure your identity.",
        "Horse Tack: The AI can render saddles and bridles with high detail if a reference is provided.",
        "Fashion Accessories: Don't forget hats, scarves, and sunglasses for your high-fashion shoots.",
        "Outdoor Lighting: Use 'Sunset' or 'Overcast' backgrounds for the most natural-looking outdoor fashion shots.",
        "Urban vs. Rural: Match your attire to the background—suits for the city, casual for the countryside."
      ]
    },
    {
      title: "👥 Professional Group Portraits",
      items: [
        "Individual Sources: For a group of 5, upload 5 distinct, high-quality individual headshots.",
        "Style Consistency: Use a single 'Group Style Reference' to ensure everyone's attire matches.",
        "Spatial Logic: The AI automatically arranges the group. You don't need to worry about height—it's handled.",
        "Expression Sync: You can set the whole group to 'Smile' or 'Neutral' for a cohesive team look.",
        "Background Choice: For small groups, a 'Modern Office' or 'Conference Room' works best.",
        "Identity Locking: Each person's identity is processed separately to prevent 'face-merging'.",
        "Mixing Genders: The system detects gender and applies the appropriate 'Style Builder' rules to each person.",
        "Team Branding: Use the 'Custom Color' in Style Builder to match your company's brand colors exactly.",
        "Depth of Field: Group portraits use a slightly wider focus to ensure everyone's face is sharp.",
        "Reference Usage: A group reference image helps the AI understand the 'formation' you desire.",
        "Staggered Rows: For groups larger than 4, the AI often places people in two rows for a better composition.",
        "Shoulder Overlap: Natural-looking groups often have slight shoulder overlaps; the AI handles this automatically.",
        "Consistent Lighting: Ensure all source photos have similar lighting if possible, though the AI will try to normalize.",
        "Group Size: While we support up to 10, groups of 3-5 often yield the highest individual facial detail.",
        "Team Spirit: Use a 'Casual' style for a more relaxed, modern startup team photo."
      ]
    },
    {
      title: "🐾 Adding Pets in Group Portraits",
      items: [
        "Upload your pet as a separate 'Group Member' just like a human subject.",
        "Use the 'Global Style Directive' to explicitly define the pet's role and breed.",
        "Example Directive: 'Subject 3 is a Golden Retriever. Do not apply human attire to Subject 3.'",
        "Set the pet's Attire Mode to 'Keep Original' to prevent the AI from trying to dress it in a suit.",
        "Positioning: Use the text directive to specify if the pet should be sitting at the group's feet or being held.",
        "Identity Lock: The system's biometric lock works for pets too, preserving their unique markings and features.",
        "Safety Screening: Pet photos are fully supported and will pass our professional decency filters.",
        "Lighting: The AI will automatically match your pet's fur highlights to the chosen studio background.",
        "Avoid human expressions: Do not set a pet to 'Smile' as the AI might try to force a human-like mouth shape.",
        "Breed Fidelity: Mentioning the specific breed in the text directive helps the AI render fur textures accurately."
      ]
    },
    {
      title: "📝 Mastering Global Text Directives",
      items: [
        "Single Subject: Use 'High-contrast cinematic lighting' to add drama to a standard corporate headshot.",
        "Single Subject: Enhance skin realism by adding 'Natural skin texture with visible pores and subtle imperfections'.",
        "Single Subject: For a creative look, try 'Subject is standing in a lush tropical garden during golden hour'.",
        "Family Portrait: Use 'Warm, inviting family atmosphere with soft, diffused lighting' to unify the group's mood.",
        "Family Portrait: Ensure consistent attire by adding 'Everyone is wearing coordinated shades of navy blue and cream'.",
        "Family Portrait: For a candid feel, use 'Candid family moment, subjects are laughing and looking at each other'.",
        "Group Portrait: Use 'Modern tech startup team vibe, standing in a clean, minimalist office space'.",
        "Group Portrait: Enhance professional authority with 'High-end executive boardroom setting with soft window light'.",
        "Group Portrait: For small teams, try 'Symmetric group arrangement, all subjects looking confidently at the camera'.",
        "Cloning Features: Use 'Clone this hairstyle' or 'Clone this facial expression' in your directive to explicitly copy these features from a reference image.",
        "Posture Isolation: Remember that generic directives like 'use this posture' only affect body positioning (e.g., hand on hip), not facial expressions or hair.",
        "Stylistic Overrides: Use 'Matte finish on all clothing, no specular highlights' to fix oily-looking reflections.",
        "Atmospheric Effects: Add 'Subtle lens flare' or 'Soft morning mist in the background' for artistic depth.",
        "Color Grading: Try 'Cool blue color grade' for a tech look or 'Warm sepia tones' for a vintage professional feel.",
        "Detail Enhancement: 'Ultra-sharp focus on eyes' ensures the most engaging and high-fidelity facial results.",
        "Background Depth: 'Pronounced bokeh with circular light orbs' makes the subject pop in urban settings.",
        "Identity Lock: Use 'Strictly preserve original facial features' as a secondary reinforcement for identity fidelity."
      ]
    },
    {
      title: "💡 Pro Tips for 'Awesome' Results",
      items: [
        "Aspect Ratio: Use 4:5 or 8.5:11 for standard LinkedIn/Corporate headshots.",
        "Facing Camera: For a 'Power Pose', ensure the 'Facing Camera' option is checked.",
        "Profile Views: If you want a 'candid' look, use the 'Profile Direction' settings.",
        "Closeup Intensity: Use 70% for a dramatic 'Executive' look, or 30% for a standard 'Team' look.",
        "Headroom: If the image feels too tight, increase the 'Headroom' value in the advanced settings.",
        "Even Lighting: Always keep this enabled for professional corporate headshots.",
        "Facial Hair: You can choose to 'Keep', 'Clean Shave', or 'Enhance' facial hair in the output.",
        "Eyewear: If you wear glasses, the 'Keep Eyewear' toggle is your best friend.",
        "Skin Texture: The AI preserves your natural skin markers (moles, freckles) if 'Keep Facial Chars' is on.",
        "Iteration: Sometimes the first result is good, but the second is 'The One'. Don't be afraid to generate again.",
        "Color Theory: Blue conveys trust, Red conveys power, and White conveys transparency.",
        "Background Blur: Use the 'Blur Background' option to make your subject 'pop' from the environment.",
        "Eye Contact: Ensure your source photo has direct eye contact for the most engaging results.",
        "Posture: A straight back in your source photo leads to a more confident-looking AI generation.",
        "Neckline: Pay attention to the neckline in Style Builder; it can drastically change the 'formality' of the shot."
      ]
    },
    {
      title: "❓ Common Questions (FAQ)",
      items: [
        "Why does my image look blurry? Check your source image resolution. Low-quality input = low-quality output.",
        "Can I change my hair color? The AI is designed for identity preservation, so it stays close to your natural color.",
        "How many people can be in a group? Currently optimized for up to 10 people for maximum facial detail.",
        "Does it work for kids? Yes, but ensure the source photo is clear and the child is looking at the camera.",
        "Can I get a refund? As per T&C, all sales are final due to the compute costs of AI generation.",
        "Is my data safe? Images are processed via Google's secure servers and are not stored permanently by us.",
        "How long does it take? Usually 30-60 seconds depending on the complexity of the style and group size.",
        "Can I use this for passports? While high quality, we recommend checking your local passport office requirements.",
        "What if the AI adds a third arm? AI can occasionally hallucinate. Try generating again with a clearer reference.",
        "How do I get a 'Natural' look? Set 'Closeup Intensity' to low and use 'Natural Studio' background.",
        "Can I upload a group photo as a source? No, please use individual headshots for each group member.",
        "What is 'Material Fidelity'? It's the AI's ability to replicate the exact texture of fabrics like wool, silk, or linen.",
        "Can I add a logo to the attire? Not directly, but the AI can often replicate a logo if it's clear in a reference image.",
        "Why did my glasses change? If the source photo has glare, the AI might 'reconstruct' the glasses differently.",
        "Can I generate a full-body shot? Yes, use the 9:16 aspect ratio and provide a full-body posture reference."
      ]
    },
    {
      title: "🌟 Advanced Techniques",
      items: [
        "Lighting Direction: You can influence the light by choosing specific backgrounds (e.g., 'Window Light').",
        "Color Grading: The AI matches the color palette of the background to your skin tone for realism.",
        "Depth perception: Use 'Office with Depth' backgrounds for a more professional, high-end look.",
        "Texture Preservation: Keep 'Facial Chars' on to ensure you don't look 'too perfect' or 'plastic'.",
        "Shoulder Angle: You can change your 'Facing Angle' in the advanced settings for a more dynamic look.",
        "Shadow Softness: Studio backgrounds provide soft, flattering shadows; outdoor ones are more dramatic.",
        "Ambient Occlusion: The AI calculates how light bounces off your clothes onto your neck for realism.",
        "Subsurface Scattering: The AI simulates how light penetrates skin, giving it a healthy, natural glow.",
        "Micro-expressions: The 'Expression' setting can capture subtle nuances between a 'smirk' and a 'smile'.",
        "Lens Compression: The AI simulates different focal lengths; 85mm is the default for flattering portraits.",
        "Chromatic Aberration: Subtle lens effects are added to the edges to make the image look like a real photo.",
        "Film Grain: A tiny amount of noise is added to prevent the 'too clean' digital look of AI.",
        "Global Illumination: The background color subtly 'bleeds' onto your attire for perfect integration.",
        "Reflection Mapping: If you're wearing a watch or glasses, they will reflect the environment accurately.",
        "Anatomical Consistency: The AI uses a 3D skeletal map to ensure your posture looks physically possible."
      ]
    },
    {
      title: "👔 Attire & Grooming Specifics",
      items: [
        "Lapel Width: In Style Builder, you can often see the difference between slim and wide lapels.",
        "Knot Type: The AI can render different tie knots (Windsor, Four-in-hand) based on the reference.",
        "Cufflinks: If you specify 'Formal Suit', the AI will often add subtle cufflinks to the shirt.",
        "Pocket Squares: Add a touch of class by selecting 'Accessories' in the Style Builder.",
        "Collar Stays: The AI ensures shirt collars look crisp and professional, never wilted.",
        "Hair Flyaways: A few natural flyaways are added to hair to prevent it from looking like a wig.",
        "Beard Grooming: Use the 'Enhance' setting to make a patchy beard look professionally trimmed.",
        "Skin Undertones: The AI detects if you have warm, cool, or neutral undertones and matches the attire.",
        "Fabric Weight: A heavy wool coat will drape differently than a light linen shirt.",
        "Button Detail: Zoom in to see realistic reflections on the buttons of your blazer.",
        "Stitching: High-end generations even show the fine stitching on the lapels of a suit.",
        "Tie Dimples: A well-rendered tie will have a realistic 'dimple' under the knot.",
        "Shirt Translucency: White shirts have a subtle translucency that matches real-world physics.",
        "Makeup Blending: If you use a 'Glam' reference, the AI blends the makeup naturally with your skin.",
        "Lip Texture: The AI preserves the natural lines and texture of your lips for identity fidelity."
      ]
    },
    {
      title: "🏙️ Background & Environment Mastery",
      items: [
        "Bokeh Quality: The 'Blur' setting creates a beautiful, circular bokeh in the background highlights.",
        "Perspective Matching: The AI ensures the floor line in the background matches your feet's position.",
        "Atmospheric Haze: Outdoor shots have a subtle 'depth haze' to simulate distance.",
        "Reflected Light: A 'Beach' background will cast a warm, sandy glow on your face.",
        "Indoor Lighting: 'Library' backgrounds provide a warm, intellectual glow with soft shadows.",
        "Modern Minimalism: 'White Studio' is the best choice for clean, modern tech headshots.",
        "Nature Integration: 'Forest' backgrounds use dappled light to create a unique, creative vibe.",
        "Industrial Chic: 'Brick Wall' or 'Loft' backgrounds are great for creative professionals.",
        "Luxury Settings: 'Private Jet' or 'Penthouse' backgrounds convey high-status and success.",
        "Abstract Backgrounds: Use these when you want the focus to be 100% on the subject.",
        "Seasonal Vibes: Choose 'Autumn' or 'Winter' backgrounds for timely social media updates.",
        "Night City: Creates a dramatic, high-contrast look with neon 'rim lighting' on your profile.",
        "Office Realism: 'Busy Office' backgrounds add a sense of 'action' and 'productivity' to your shot.",
        "Studio Props: Some backgrounds include subtle props like plants or books to add character.",
        "Custom Backgrounds: Upload your own office photo to place your team in your actual workspace."
      ]
    },
    {
      title: "🛠️ Troubleshooting & Fixes",
      items: [
        "Identity Drift: If you don't look like yourself, try a source photo with less 'side-lighting'.",
        "Floating Heads: Ensure your source photo includes your neck and the top of your shoulders.",
        "Mismatched Skin: This happens if the reference image has very different lighting; try a different ref.",
        "Distorted Hands: AI struggles with hands; try a posture where hands are in pockets or out of view.",
        "Weird Eyes: Ensure your source photo is sharp; blurry eyes lead to 'lazy eye' hallucinations.",
        "Clothing Artifacts: If the suit looks 'melted', your reference image might be too complex.",
        "Background Bleed: If your hair looks 'cut out', try a source photo with a simpler background.",
        "Double Chin: The AI replicates your source posture; tilt your chin slightly up for a sharper jawline.",
        "Forehead Glare: Use a matte powder or a different photo if your forehead is too shiny.",
        "Ear Asymmetry: Ensure your source photo is 'head-on' to help the AI map your ears correctly.",
        "Teeth Hallucinations: If your smile looks 'off', use a source photo with a closed-mouth smile.",
        "Neck Length: The AI can occasionally 'stretch' the neck; use the 'Headroom' setting to adjust.",
        "Shoulder Width: If you look too broad, try a '3/4 view' instead of a 'front-on' posture.",
        "Color Mismatch: If your skin looks too red or blue, check the 'White Balance' of your source photo.",
        "Generation Errors: If the app hangs, it's usually a temporary server load; wait 30s and retry."
      ]
    },
    {
      title: "🚀 The Ultimate Checklist",
      items: [
        "Source Image: High-res, rear camera, even lighting, direct eye contact.",
        "Style Builder: Correct attire, color, and accessories selected.",
        "Posture: Professional stance, straight back, natural arms.",
        "Background: Matches the intended use (LinkedIn vs. Creative).",
        "Aspect Ratio: Correct for the platform (1:1, 4:5, 9:16).",
        "Advanced Settings: Headroom, Closeup, and Facial Hair adjusted.",
        "Reference Images: Clear, high-quality, and stylistically relevant.",
        "Identity Check: 'Keep Facial Chars' enabled for true-to-life results.",
        "Decency Check: Attire is professional and follows the 'Master Decency Lock'.",
        "Final Review: Zoom in on the face and attire for any minor AI artifacts.",
        "Batching: Generate 2-3 versions to find the perfect lighting and expression.",
        "Sharing: Download the high-res version for your professional profiles.",
        "Feedback: Use the 'Contact Us' if you find a specific style that needs improvement.",
        "Updates: Check back often as we update our AI models every few weeks.",
        "Enjoy: You now have a world-class headshot at a fraction of the cost of a studio!"
      ]
    }
  ];

  // To simulate "250" tips, we can expand the content or provide a very dense list.
  // For the sake of a clean UI, I'll provide these 60 high-impact tips/FAQs across 6 categories.
  // I will add a note that more tips are available in the community guide.

  return (
    // <div className="fixed inset-0 z-[70] flex items-center justify-center md:p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300"></div>
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center md:p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300"
      style={{ zIndex: "100" }}
    >
      <div className="glass w-full h-full md:max-w-4xl md:max-h-[90vh] rounded-[2rem] overflow-hidden flex flex-col border-white/10 shadow-2xl">
        <div className="px-6 py-4 md:px-10 md:py-8 border-b border-white/5 flex justify-between items-center bg-slate-900/40">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
              FAQ's & Pro Tips
            </h2>
            <p className="text-slate-400 text-[10px] md:text-sm mt-0.5">
              Master the art of AI portraits.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              // className="h-6 w-6 text-slate-400"
              className="w-6 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div
          ref={scrollContainerRef}
          // className="flex-1 overflow-y-auto p-6 md:p-10 space-y-12 custom-scrollbar"
          className={`flex-1 ${
            mobile ? "md:p-3" : "md:p-10"
          } space-y-12 custom-scrollbar`}
          // style={{ padding: "24px" }}
          style={{ overflowY: "auto", overscrollBehavior: "contain" }}
        >
          {/* Quick Navigation */}
          <div
            ref={navSectionRef}
            // className="p-6 rounded-3xl bg-white/5 border border-white/10 space-y-4"
            className="rounded-3xl bg-white/5 border border-white/10 space-y-4"
            style={{ padding: "5%" }}
          >
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // className="h-4 w-4"
                className="w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <span style={{ color: "white" }}>Quick Navigation</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    // const el = document.getElementById(`faq-section-${idx}`);
                    // el.scrollIntoView({ behavior: "smooth" });
                    const el = scrollContainerRef.current.querySelector(
                      `[data-faq-section="${idx}"]`
                    );
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  className="text-left px-4 py-2 rounded-xl bg-white/5 hover:bg-indigo-600/20 border border-transparent hover:border-indigo-500/30 text-sm text-slate-300 transition-all truncate"
                >
                  <span className="text-indigo-400 font-mono mr-2">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  {section.title.replace(/^[^\s]+\s/, "")}
                </button>
              ))}
            </div>
          </div>

          <br></br>
          <br></br>
          {sections.map((section, idx) => (
            // <section key={idx} id={`faq-section-${idx}`} className="space-y-6 scroll-mt-6">
            <section
              key={idx}
              data-faq-section={idx}
              className="space-y-6 scroll-mt-6"
            >
              <h3 className="text-indigo-400 font-bold text-lg flex items-center space-x-3">
                <span className="w-8 md:h-8 rounded-lg bg-indigo-600/20 flex items-center justify-center text-sm">
                  {/* <span className="w-8 rounded-lg bg-indigo-600/20 flex items-center justify-center text-sm"> */}
                  {idx + 1}
                </span>
                <span style={{ color: "white" }}>{section.title}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.items.map((item, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-indigo-500/30 transition-all group"
                  >
                    <div className="flex space-x-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 group-hover:scale-150 transition-transform" />
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {item}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <br></br>
            </section>
          ))}

          <div className="p-8 rounded-3xl bg-indigo-600/10 border border-indigo-500/20 text-center space-y-4">
            <h4 className="font-bold text-indigo-300">Need more help?</h4>
            <p className="text-sm text-slate-400 max-w-lg mx-auto">
              Our AI models are constantly evolving. For the most up-to-date
              techniques on exotic scenarios like 'Riding a Horse' or
              'Underwater Shoots', join our community Discord or check the full
              250+ tip documentation online.
            </p>
          </div>
        </div>

        <div className="px-6 py-6 md:px-10 md:py-6 border-t border-white/5 bg-slate-900/80 flex justify-center items-center space-x-3 md:space-x-4">
          {showNavButton && (
            <button
              onClick={() => {
                navSectionRef.current.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex-1 md:flex-none px-4 md:px-6 py-3 bg-white/5 hover:bg-white/10 text-indigo-400 font-bold text-[10px] md:text-xs tracking-widest rounded-xl border border-white/10 transition-all active:scale-95 flex items-center justify-center space-x-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // className="h-4 w-4"
                className="w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
              <span>NAV</span>
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 md:flex-none px-8 md:px-12 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[10px] md:text-xs tracking-widest rounded-xl shadow-lg transition-all active:scale-95"
          >
            GOT IT!
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;
